import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useField } from "formik"

import {
  InputContainer,
  InputFieldLabel,
  InputFieldIcon,
  ErrorMessage,
  DisabledMessage,
} from "./common"

import { mixins } from "../../styles"

// import CheckmarkSVG from "../../images/icons/check--small.inline.svg"
import CrossSVG from "../../images/icons/cross--small.inline.svg"

const MultiselectField = styled.select`
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  display: block;
  ${mixins.copy5};
  margin: 10px 30px 5px 0;
  width: calc(100% - 30px);

  @media (pointer: coarse) {
    /*
      Safari iOS does not display multiple lines on <select> or background colors
      on <option> so we must style it to look like a regular select here
    */
    border-bottom: 1px solid var(--color-sage);
  }

  option {
    background: var(--color-white);
    padding: 5px 10px;
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
    `}

  ${(props) =>
    props.$empty &&
    css`
      color: rgba(var(--color-spaceblueRGB), 0.5);
    `}

  ${(props) =>
    props.$error &&
    css`
      border: 1px solid var(--color-error);
    `}

  &:focus {
    /* border-color: var(--color-sun); */
    outline: none;
  }
`

const Multiselect = ({
  className,
  children,
  disabled,
  forwardRef,
  hidden,
  hideLabel,
  hideRequired,
  label,
  onBlur,
  onFocus,
  placeholder,
  required,
  ...props
}) => {
  const [field, meta] = useField(props)
  const [active, setActive] = useState(false)
  // const [ok, setOk] = useState(false)
  const fieldId = props.id || field.name
  const hasDisabledMessage = typeof disabled === "string"
  const hasError = meta.touched && meta.error

  if (placeholder) {
    throw new Error("Don't use a placeholder for multiselects")
  }

  useEffect(() => {
    if (field.value || placeholder) {
      setActive(true)
    }

    // if (field.value.length < 1 || meta.error) {
    //   setOk(false)
    // } else {
    //   setOk(true)
    // }
  }, [field.value, meta.error, placeholder])

  const handleFocus = (callback) => {
    setActive(true)
    // setOk(false)
    callback && callback()
  }

  const handleBlur = (callback) => {
    if (!field.value && !placeholder) {
      setActive(false)
    }
    // if (field.value.length > 0 && !meta.error) {
    //   setOk(true)
    // }
    callback && callback()
  }

  const describedByIds = [
    hasDisabledMessage ? `${fieldId}-disabled` : null,
    hasError ? `${fieldId}-error` : null,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <InputContainer
      className={className}
      data-testid={`multiselect-${fieldId}`}
      hidden={hidden}
    >
      <InputFieldLabel
        $active={active}
        hidden={hideLabel}
        htmlFor={fieldId}
        id={`${fieldId}-label`}
      >
        {label} {required && !hideRequired && "*"}
      </InputFieldLabel>
      <MultiselectField
        {...field}
        {...props}
        aria-describedby={describedByIds || undefined}
        aria-invalid={hasError ? "true" : undefined}
        aria-labelledby={`${fieldId}-label`}
        aria-required={required}
        disabled={disabled}
        $empty={!field.value}
        $error={hasError ? "true" : undefined}
        id={fieldId}
        multiple
        onBlur={() => handleBlur(onBlur)}
        onFocus={() => handleFocus(onFocus)}
        ref={forwardRef}
      >
        {children}
      </MultiselectField>
      {/* {ok && (
        <InputFieldIcon data-testid="checkmark">
          <CheckmarkSVG />
        </InputFieldIcon>
      )} */}
      {hasError ? (
        <>
          {meta.error && (
            <InputFieldIcon $error data-testid="errorcross">
              <CrossSVG />
            </InputFieldIcon>
          )}
          <ErrorMessage id={`${fieldId}-error`} role="alert">
            {meta.error}
            {hasDisabledMessage && (
              <DisabledMessage
                data-testid={`${fieldId}-disabled`}
                id={`${fieldId}-disabled`}
              >
                {disabled}
              </DisabledMessage>
            )}
          </ErrorMessage>
        </>
      ) : null}
      {hasDisabledMessage && !meta.error && (
        <DisabledMessage
          data-testid={`${fieldId}-disabled`}
          id={`${fieldId}-disabled`}
        >
          {disabled}
        </DisabledMessage>
      )}
    </InputContainer>
  )
}

Multiselect.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  forwardRef: PropTypes.object,
  hidden: PropTypes.bool,
  hideRequired: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  required: PropTypes.bool,
}

export default Multiselect
